/**
 * A fake sign-in for local development (quickstart.md Part 1). Any email
 * "signs in" instantly with no password; the configured owner email gets
 * role 'owner', everyone else gets 'member'. Replaced for real by Cognito
 * email+password sign-in in User Story 3 (approved people only, per
 * FR-024/FR-025) — the Session interface here is what app-router.tsx and
 * every screen are built against, so swapping the real implementation in
 * later does not touch UI code.
 */

/**
 * The default owner email for local development's fake sign-in. Overridable
 * via VITE_OWNER_EMAIL. The real owner identity (User Story 3) comes from
 * Cognito's `owner` group instead.
 */
const DEFAULT_OWNER_EMAIL = 'owner@example.com';

export function configuredOwnerEmail(): string {
  return (import.meta.env.VITE_OWNER_EMAIL as string | undefined) ?? DEFAULT_OWNER_EMAIL;
}

export type Role = 'owner' | 'member';

export interface Person {
  /** Becomes the person's cellar id (contracts/storage-and-permissions.md). */
  id: string;
  email: string;
  role: Role;
}

export type SignInResult =
  | { kind: 'signedIn'; person: Person }
  /** A newly invited person must choose their own password first (research D4, FR-045). */
  | { kind: 'newPasswordRequired' };

/**
 * Why a sign-in step failed. `refused` covers wrong password, unknown address and stopped access
 * alike, so the page never reveals which (FR-024, contracts/ui-and-emails.md).
 */
export type SignInFailure =
  'refused' | 'invalidPassword' | 'invalidCode' | 'tooManyAttempts' | 'network';

export class SignInError extends Error {
  constructor(public readonly reason: SignInFailure) {
    super(`Sign-in failed: ${reason}.`);
    this.name = 'SignInError';
  }
}

/**
 * Sign-in as the app sees it. Implemented by the fake below (local development, previews and
 * tests: any email, no password) and by `cognito-session.ts` (the real user pool, T074a).
 */
export interface Session {
  /** False for the fake: the sign-in page then asks for an email only. */
  readonly usesPasswords: boolean;
  getCurrentPerson(): Person | undefined;
  /** Signs back in from a saved session when the app opens (FR-028), if there is one. */
  restore(): Promise<Person | undefined>;
  signIn(email: string, password: string): Promise<SignInResult>;
  /** After `newPasswordRequired`: sets the person's own password and signs them in. */
  completeNewPassword(newPassword: string): Promise<Person>;
  /** Forgotten password: emails a one-time code. */
  startPasswordReset(email: string): Promise<void>;
  finishPasswordReset(email: string, code: string, newPassword: string): Promise<void>;
  signOut(): Promise<void>;
  onChange(listener: (person: Person | undefined) => void): () => void;
}

export interface FakeSessionOptions {
  /** The email that signs in as the owner; every other email is a member. */
  ownerEmail: string;
  /**
   * False for a person the owner stopped on the people screen (fake-directory.ts), who is then
   * refused like Cognito would refuse them. Anyone else may sign in.
   */
  canSignIn?: (email: string) => boolean;
}

/** The fake session's cellar id for an email (also used to seed demo data). */
export function fakeIdFor(email: string): string {
  return `fake-${email.toLowerCase().trim()}`;
}

export function createFakeSession(options: FakeSessionOptions): Session {
  let current: Person | undefined;
  const listeners = new Set<(person: Person | undefined) => void>();

  function notify(): void {
    for (const listener of listeners) {
      listener(current);
    }
  }

  return {
    usesPasswords: false,

    getCurrentPerson(): Person | undefined {
      return current;
    },

    async restore(): Promise<Person | undefined> {
      // Nothing survives a reload in the fake: previews start signed out.
      return undefined;
    },

    async signIn(email: string): Promise<SignInResult> {
      const normalised = email.toLowerCase().trim();
      if (options.canSignIn && !options.canSignIn(normalised)) {
        throw new SignInError('refused');
      }
      current = {
        id: fakeIdFor(normalised),
        email: normalised,
        role: normalised === options.ownerEmail.toLowerCase().trim() ? 'owner' : 'member',
      };
      notify();
      return { kind: 'signedIn', person: current };
    },

    // The fake has no passwords: these steps never happen on previews.
    async completeNewPassword(): Promise<Person> {
      throw new SignInError('refused');
    },
    async startPasswordReset(): Promise<void> {},
    async finishPasswordReset(): Promise<void> {},

    async signOut(): Promise<void> {
      current = undefined;
      notify();
    },

    onChange(listener: (person: Person | undefined) => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
